/**
 * A shed drawn in ink, in oblique projection, at any stage of being built.
 *
 * The front is drawn true and the depth recedes up and to the right, the way an
 * illustrator draws a building on squared paper. Everything is placed in feet — `x`
 * across the front, `y` up from the ground, `z` back from the front — and projected
 * once, so a wall framed flat on the deck and stood up is the same wall rotated about
 * its bottom plate, not a second drawing of it.
 *
 * The order of the work is the build guide's: runners, the floor frame, the deck — or,
 * for a shed on a slab, the slab poured and the sills bolted down to it — then the walls
 * framed flat and stood up, the cap plates, the rafters, the siding, the roof, the doors.
 * Each stage is a number from 0 to 1 in `Progress`, and a film moves them.
 */

import type { Ink, Paint, Pt } from "./ink.js";
import { clamp, ease, mix, span } from "./timing.js";

export interface Shape {
  width: number;
  depth: number;
  /** The wall's height from the deck to the top of the plates. */
  wall: number;
  /** How far the ridge stands above the walls. */
  rise: number;
  /** Where the ridge is across the width: half for a gable, one for a lean-to. */
  ridge: number;
  doubleDoor: boolean;
  /** How many windows along the right-hand wall, spread evenly down it. */
  windows: number;
  /** What it stands on: runners under a framed floor, or a slab with sills bolted to it. */
  foundation: "runners" | "slab";
}

export const TEN_BY_TWELVE: Shape = {
  width: 10,
  depth: 12,
  wall: 7,
  rise: 3.3,
  ridge: 0.5,
  doubleDoor: true,
  windows: 1,
  foundation: "runners",
};

export interface WallProgress {
  /** How many of its members are on the deck. */
  frame: number;
  /** From lying flat on the deck (0) to standing plumb (1). */
  raise: number;
}

export interface Progress {
  runners: number;
  joists: number;
  deck: number;
  /** On a slab: the forms set, the concrete poured and struck off level. */
  slab: number;
  /** On a slab: the treated sills laid on it and bolted down. */
  sills: number;
  left: WallProgress;
  back: WallProgress;
  right: WallProgress;
  front: WallProgress;
  caps: number;
  rafters: number;
  siding: number;
  roofDeck: number;
  shingles: number;
  trim: number;
  doors: number;
  /** The doors, from shut (0) to wide open (1). */
  swing: number;
}

const STANDING: WallProgress = { frame: 1, raise: 1 };

export const BUILT: Progress = {
  runners: 1,
  joists: 1,
  deck: 1,
  slab: 1,
  sills: 1,
  left: STANDING,
  back: STANDING,
  right: STANDING,
  front: STANDING,
  caps: 1,
  rafters: 1,
  siding: 1,
  roofDeck: 1,
  shingles: 1,
  trim: 1,
  doors: 1,
  swing: 0,
};

const FLAT: WallProgress = { frame: 0, raise: 0 };

export const NOTHING: Progress = {
  runners: 0,
  joists: 0,
  deck: 0,
  slab: 0,
  sills: 0,
  left: FLAT,
  back: FLAT,
  right: FLAT,
  front: FLAT,
  caps: 0,
  rafters: 0,
  siding: 0,
  roofDeck: 0,
  shingles: 0,
  trim: 0,
  doors: 0,
  swing: 0,
};

/** Where the shed stands and how big a foot is: the front-left corner at ground level. */
export interface Projection {
  x: number;
  y: number;
  /** Stage units per foot. */
  scale: number;
}

const KX = 0.42;
const KY = 0.4;

export interface Vec {
  x: number;
  y: number;
  z: number;
}

export function project(proj: Projection, x: number, y: number, z: number): Pt {
  return { x: proj.x + (x + z * KX) * proj.scale, y: proj.y - (y + z * KY) * proj.scale };
}

const RUNNER = 0.4;
const JOIST = 0.55;
const SHEET = 0.08;
/** The deck's top: what the walls stand on. */
export const FLOOR = RUNNER + JOIST + SHEET;
/** A slab's top above the ground, and a sill's thickness on it. */
export const SLAB = 0.45;
const SILL = 0.16;
/** How thick a form board is, and how far its top stands over the slab's. */
const FORM = 0.12;
const STUD = 0.2;
const PLATE = 0.22;
const OVERHANG = 0.7;
const RAFTER = 0.5;

/** What a shape's walls stand on: the deck, or the sills on the slab. */
export function floorOf(shape: Shape): number {
  return shape.foundation === "slab" ? SLAB + SILL : FLOOR;
}

/** Where the siding stops at the bottom: lapped over the rim, or just above the concrete. */
function sidingBottom(shape: Shape): number {
  return shape.foundation === "slab" ? SLAB + 0.03 : RUNNER + 0.1;
}

/** How far a member at `index` of `count` has appeared, as its stage goes from 0 to 1. */
function appear(progress: number, index: number, count: number, overlap = 3): number {
  const [start, end] = arrival(index, count, overlap);
  return span(progress, start, end);
}

/** The stretch of its stage over which piece `index` of `count` arrives. */
function arrival(index: number, count: number, overlap: number): [number, number] {
  const each = 1 / (count + overlap - 1);
  return [index * each, (index + overlap) * each];
}

/**
 * How each stage's pieces arrive: how many are on their way at once (`overlap`), and how
 * far through its own arrival a piece first touches down (`lands`) — where the bounce or
 * the spring in its easing meets the surface it lands on. The drawing reads the overlaps;
 * `landings` reads both, which is how a soundtrack knocks when the picture does.
 */
const STAGING = {
  runners: { overlap: 1.6, lands: 0.72 },
  joists: { overlap: 2, lands: 0.72 },
  deck: { overlap: 1.4, lands: 0.8 },
  wall: { overlap: 2.5, lands: 0.3 },
  rafters: { overlap: 2, lands: 0.35 },
  siding: { overlap: 1.5, lands: 0.35 },
} as const;

/** The rafter pairs, back to front. */
function rafterLines(depth: number): number[] {
  const zs: number[] = [];
  for (let z = depth; z > 0.2; z -= 2) {
    zs.push(z);
  }
  zs.push(0);
  return zs;
}

/** The joists, back to front, after the two rims. */
function joistLines(depth: number): number[] {
  const zs: number[] = [];
  for (let z = depth - 0.15; z > 0.1; z -= 4 / 3) {
    zs.push(z);
  }
  zs.push(0);
  return zs;
}

export interface Landings {
  runners: number[];
  joists: number[];
  deck: number[];
  /** A slab's forms, as a fraction of the slab's stage: they are set in its first stretch. */
  forms: number[];
  sills: number[];
  walls: Record<"left" | "back" | "right" | "front", number[]>;
  rafters: number[];
  siding: number[];
  roofDeck: number[];
  doors: number[];
}

/**
 * When each piece of a shed lands, as a fraction of its own stage: for whatever has to
 * happen when the picture shows a board touching down.
 */
export function landings(shape: Shape): Landings {
  const at = (count: number, staging: { overlap: number; lands: number }, from = 0, to = 1): number[] =>
    Array.from({ length: count }, (_, i) => {
      const [start, end] = arrival(i, count, staging.overlap);
      return from + (to - from) * (start + (end - start) * staging.lands);
    });
  const defs = walls(shape);
  const memberCount = (name: Wall["name"]): number => defs.find((wall) => wall.name === name)!.members.length;
  const sheets = Math.ceil(shape.depth / 4) + Math.ceil(shape.width / 4);
  return {
    runners: at(3, STAGING.runners),
    joists: at(2 + joistLines(shape.depth).length, STAGING.joists),
    deck: at(3, STAGING.deck),
    forms: at(4, STAGING.runners, 0, FORMS_SET),
    sills: at(sillPieces(shape).length, STAGING.joists),
    walls: {
      left: at(memberCount("left"), STAGING.wall),
      back: at(memberCount("back"), STAGING.wall),
      right: at(memberCount("right"), STAGING.wall),
      front: at(memberCount("front"), STAGING.wall),
    },
    rafters: at(rafterLines(shape.depth).length, STAGING.rafters, 0.12, 1),
    siding: at(sheets, STAGING.siding),
    // Two slopes, each over its own stretch (`drawRoof`), touching down where the spring does.
    roofDeck: [0, 1].map((i) => i * 0.35 + 0.65 * STAGING.siding.lands),
    // The leaves, likewise (`drawDoors`).
    doors: (shape.doubleDoor ? [0, 1] : [0]).map((i) => i * 0.3 + 0.7 * STAGING.siding.lands),
  };
}

interface Rect {
  u0: number;
  u1: number;
  v0: number;
  v1: number;
}

interface Wall {
  name: "left" | "back" | "right" | "front";
  origin: Vec;
  /** Along the wall, as it is seen from outside, left to right. */
  along: Vec;
  /** From the bottom plate across the deck: where the top plate lies while it is flat. */
  inward: Vec;
  length: number;
  members: Rect[];
  /** The rough openings, framed: where the siding is cut and a door or window goes. */
  openings: Rect[];
}

function add(a: Vec, b: Vec, k = 1): Vec {
  return { x: a.x + b.x * k, y: a.y + b.y * k, z: a.z + b.z * k };
}

/** The studs, plates and openings of one wall, in its own (along, up) feet. */
function framing(length: number, wall: number, openings: readonly Rect[]): Rect[] {
  const members: Rect[] = [
    { u0: 0, u1: length, v0: 0, v1: PLATE },
    { u0: 0, u1: length, v0: wall - PLATE, v1: wall },
  ];
  const kings = openings.map((opening) => [opening.u0 - STUD * 2, opening.u1 + STUD * 2] as const);
  const studs: number[] = [];
  for (let u = 0; u < length - STUD; u += 4 / 3) {
    studs.push(u);
  }
  studs.push(length - STUD);
  for (const u of studs) {
    if (kings.some(([kingLeft, kingRight]) => u + STUD > kingLeft && u < kingRight)) {
      continue;
    }
    members.push({ u0: u, u1: u + STUD, v0: PLATE, v1: wall - PLATE });
  }
  openings.forEach((opening, i) => {
    const [kingLeft, kingRight] = kings[i]!;
    const header = 0.55;
    members.push(
      { u0: kingLeft, u1: kingLeft + STUD, v0: PLATE, v1: wall - PLATE },
      { u0: opening.u0 - STUD, u1: opening.u0, v0: PLATE, v1: opening.v1 },
      { u0: opening.u1, u1: opening.u1 + STUD, v0: PLATE, v1: opening.v1 },
      { u0: kingRight - STUD, u1: kingRight, v0: PLATE, v1: wall - PLATE },
      { u0: opening.u0 - STUD, u1: opening.u1 + STUD, v0: opening.v1, v1: opening.v1 + header },
    );
    if (opening.v0 > 0.5) {
      members.push({ u0: opening.u0, u1: opening.u1, v0: opening.v0 - PLATE, v1: opening.v0 });
      for (let u = opening.u0 + 4 / 3; u < opening.u1 - 0.3; u += 4 / 3) {
        members.push({ u0: u, u1: u + STUD, v0: PLATE, v1: opening.v0 - PLATE });
      }
    }
  });
  // Plates first, then left to right: the order a framer lays them on the deck.
  return members.sort((a, b) => (a.v1 - a.v0 > 1 ? 1 : 0) - (b.v1 - b.v0 > 1 ? 1 : 0) || a.u0 - b.u0);
}

function doorOpening(shape: Shape): Rect {
  const width = shape.doubleDoor ? Math.min(5, shape.width - 2.4) : 3;
  const u0 = (shape.width - width) / 2;
  return { u0, u1: u0 + width, v0: 0, v1: shape.wall - PLATE - 0.6 };
}

/** The windows down the right-hand wall, evenly spaced. */
function windowOpenings(shape: Shape): Rect[] {
  return Array.from({ length: shape.windows }, (_, i) => {
    const middle = (shape.depth * (i + 1)) / (shape.windows + 1);
    return { u0: middle - 1.5, u1: middle + 1.5, v0: 3.3, v1: shape.wall - PLATE - 0.6 };
  });
}

function walls(shape: Shape): Wall[] {
  const { width: w, depth: d, wall } = shape;
  const floor = floorOf(shape);
  const door = doorOpening(shape);
  const windows = windowOpenings(shape);
  return [
    {
      name: "back",
      origin: { x: w, y: floor, z: d },
      along: { x: -1, y: 0, z: 0 },
      inward: { x: 0, y: 0, z: -1 },
      length: w,
      members: framing(w, wall, []),
      openings: [],
    },
    {
      name: "left",
      origin: { x: 0, y: floor, z: d },
      along: { x: 0, y: 0, z: -1 },
      inward: { x: 1, y: 0, z: 0 },
      length: d,
      members: framing(d, wall, []),
      openings: [],
    },
    {
      name: "right",
      origin: { x: w, y: floor, z: 0 },
      along: { x: 0, y: 0, z: 1 },
      inward: { x: -1, y: 0, z: 0 },
      length: d,
      members: framing(d, wall, windows),
      openings: windows,
    },
    {
      name: "front",
      origin: { x: 0, y: floor, z: 0 },
      along: { x: 1, y: 0, z: 0 },
      inward: { x: 0, y: 0, z: 1 },
      length: w,
      members: framing(w, wall, [door]),
      openings: [door],
    },
  ];
}

/**
 * The slab's stage, in parts: the forms are set by `set`, the concrete is all in by
 * `poured`, and the rest is striking it off level. A film times its pour from these.
 */
export const SLAB_WORK = { set: 0.3, poured: 0.85 } as const;
const FORMS_SET = SLAB_WORK.set;

/**
 * The sills on a slab, as boxes in feet: down each side, across the back, and across the
 * front in two pieces, cut out of the doorway so nobody trips on it.
 */
function sillPieces(shape: Shape): [number, number, number, number][] {
  const { width: w, depth: d } = shape;
  const door = doorOpening(shape);
  const across = 0.46;
  return [
    [0, w, d - across, d],
    [0, across, 0, d],
    [w - across, w, 0, d],
    [0, door.u0, 0, across],
    [door.u1, w, 0, across],
  ];
}

/** The height of the underside of the roof above the ground at `x`, over the walls' line. */
export function roofHeight(shape: Shape, x: number): number {
  const top = floorOf(shape) + shape.wall;
  const ridgeX = shape.width * shape.ridge;
  if (x <= ridgeX || ridgeX >= shape.width - 0.01) {
    return top + shape.rise * (x / Math.max(0.01, ridgeX));
  }
  return top + shape.rise * ((shape.width - x) / (shape.width - ridgeX));
}

/** Where a point on a wall is, with the wall raised by `raise` (0 flat, 1 plumb). */
function wallPoint(wallDef: Wall, raise: number, u: number, v: number): Vec {
  const angle = (Math.PI / 2) * raise;
  const up = add({ x: 0, y: Math.sin(angle), z: 0 }, wallDef.inward, Math.cos(angle));
  return add(add(wallDef.origin, wallDef.along, u), up, v);
}

export interface Drawn {
  /** The top of the front wall's top plate at its middle, for a figure lifting it. */
  frontTop: Pt;
}

export interface Layers {
  /** Drawn on what the walls stand on, before any of them: marks in fresh concrete. */
  floor?: () => void;
  /** Drawn inside the walls: behind the front and right walls, in front of the others. */
  inside?: () => void;
  /** A light on inside, 0–1: the windows glow with it. */
  glow?: number;
}

export function drawShed(ink: Ink, proj: Projection, shape: Shape, progress: Progress, layers: Layers = {}): Drawn {
  const p = ink.palette;
  const P = (v: Vec): Pt => project(proj, v.x, v.y, v.z);
  const at = (x: number, y: number, z: number): Pt => project(proj, x, y, z);
  const { width: w, depth: d } = shape;
  const thin: Paint = { width: 1.8, overshoot: false, offset: 0.8 };
  const fine = proj.scale < 12;
  const memberPaint = (fill: string): Paint => (fine ? { ...thin, width: 1.1, fill } : { ...thin, fill });

  if (shape.foundation === "slab") {
    drawSlab(ink, proj, shape, progress, fine);
  }

  // --- runners: three skids along the depth, dropped onto the ground.
  if (shape.foundation === "runners" && progress.runners > 0) {
    [0.3, w / 2 - RUNNER / 2, w - 0.3 - RUNNER].forEach((x0, i) => {
      const k = appear(progress.runners, i, 3, STAGING.runners.overlap);
      if (k <= 0) {
        return;
      }
      const lift = (1 - ease.bounce(k)) * 4;
      box(ink, proj, x0, x0 + RUNNER, lift, lift + RUNNER, -0.3, d + 0.3, p.wood, p.woodDark, fine);
    });
  }

  // --- the floor frame: two rims, then the joists dropping in from the back.
  if (shape.foundation === "runners" && progress.joists > 0) {
    const zs = joistLines(d);
    const members: [number, number, number, number][] = [
      [0, 0.15, 0, d],
      [w - 0.15, w, 0, d],
      ...zs.map((z): [number, number, number, number] => [0.15, w - 0.15, z, z + 0.15]),
    ];
    members.forEach(([x0, x1, z0, z1], i) => {
      const k = appear(progress.joists, i, members.length, STAGING.joists.overlap);
      if (k <= 0) {
        return;
      }
      const lift = (1 - ease.bounce(k)) * 3;
      box(ink, proj, x0, x1, RUNNER + lift, RUNNER + JOIST + lift, z0, z1, p.wood, p.woodDark, fine);
    });
  }

  // --- the deck: three sheets, slapped down back to front.
  if (shape.foundation === "runners" && progress.deck > 0) {
    [8, 4, 0].forEach((z0, i) => {
      const k = appear(progress.deck, i, 3, STAGING.deck.overlap);
      if (k <= 0) {
        return;
      }
      const z1 = Math.min(d, z0 + 4);
      const y = RUNNER + JOIST + (1 - ease.out(k)) * 1.5;
      const top = y + SHEET;
      ink.shape([at(0, top, z0), at(w, top, z0), at(w, top, z1), at(0, top, z1)], {
        fill: p.wood,
        fillAlpha: 0.55 + 0.45 * k,
        width: fine ? 1.2 : 2,
        overshoot: !fine,
      });
      ink.shape([at(0, y, z0), at(w, y, z0), at(w, top, z0), at(0, top, z0)], {
        fill: p.woodDark,
        width: fine ? 1 : 1.6,
        overshoot: false,
        offset: 0,
      });
    });
  }

  layers.floor?.();

  const floor = floorOf(shape);
  const defs = walls(shape);
  const wallOf = (name: Wall["name"]): Wall => defs.find((wall) => wall.name === name)!;
  const raised = (name: Wall["name"]): number => ease.out(progress[name].raise);
  const rect = (wallDef: Wall, raise: number, r: Rect): Pt[] => [
    P(wallPoint(wallDef, raise, r.u0, r.v0)),
    P(wallPoint(wallDef, raise, r.u1, r.v0)),
    P(wallPoint(wallDef, raise, r.u1, r.v1)),
    P(wallPoint(wallDef, raise, r.u0, r.v1)),
  ];

  // --- the walls, framed flat and stood up: back and left behind, right and front ahead.
  const drawFrame = (wallDef: Wall): void => {
    const state = progress[wallDef.name];
    if (state.frame <= 0) {
      return;
    }
    const raise = raised(wallDef.name);
    wallDef.members.forEach((member, i) => {
      const k = appear(state.frame, i, wallDef.members.length, STAGING.wall.overlap);
      if (k <= 0) {
        return;
      }
      const isCap = member.v0 >= shape.wall - PLATE - 0.01 && progress.caps > 0;
      ink.shape(rect(wallDef, raise, member), {
        ...memberPaint(isCap ? mixColor(p, progress.caps) : p.wood),
        draw: ease.out(k),
        fillAlpha: k,
      });
    });
  };
  drawFrame(wallOf("back"));
  drawFrame(wallOf("left"));

  // --- the roof frame: ridge, gable studs, rafters back to front.
  const ridgeX = w * shape.ridge;
  const ridgeY = floor + shape.wall + shape.rise;
  const eaveLeft = { x: -OVERHANG, y: roofHeight(shape, -OVERHANG) };
  const eaveRight = { x: w + OVERHANG, y: roofHeight(shape, w + OVERHANG) };
  const rafterZs = rafterLines(d);
  if (progress.rafters > 0 && progress.roofDeck < 1) {
    const ridgeK = span(progress.rafters, 0, 0.2);
    if (ridgeK > 0) {
      ink.shape(
        [at(ridgeX, ridgeY, -0.2), at(ridgeX, ridgeY, d + 0.2), at(ridgeX, ridgeY - RAFTER, d + 0.2), at(ridgeX, ridgeY - RAFTER, -0.2)],
        { ...memberPaint(p.wood), draw: ease.out(ridgeK) },
      );
    }
    rafterZs.forEach((z, i) => {
      const k = appear(span(progress.rafters, 0.12, 1), i, rafterZs.length, STAGING.rafters.overlap);
      if (k <= 0) {
        return;
      }
      const drop = (1 - ease.back(k)) * 2.5;
      for (const [a, b] of [
        [eaveLeft, { x: ridgeX, y: ridgeY }],
        [{ x: ridgeX, y: ridgeY }, eaveRight],
      ] as const) {
        if (Math.abs(a.x - b.x) < 0.05) {
          continue;
        }
        ink.shape(
          [at(a.x, a.y + drop, z), at(b.x, b.y + drop, z), at(b.x, b.y - RAFTER + drop, z), at(a.x, a.y - RAFTER + drop, z)],
          { ...memberPaint(p.wood), fillAlpha: k },
        );
      }
    });
  }

  layers.inside?.();

  if (progress.siding < 1) {
    drawFrame(wallOf("right"));
    drawFrame(wallOf("front"));
  }

  // --- gable studs over the front wall, once the rafters are up.
  if (progress.rafters > 0.6 && progress.siding < 1) {
    const k = span(progress.rafters, 0.6, 1);
    for (let x = 4 / 3; x < w - 0.3; x += 4 / 3) {
      const top = roofHeight(shape, x) - 0.15;
      const bottom = floor + shape.wall;
      if (top - bottom < 0.3) {
        continue;
      }
      ink.shape([at(x, bottom, 0), at(x + STUD, bottom, 0), at(x + STUD, top, 0), at(x, top, 0)], {
        ...memberPaint(p.wood),
        fillAlpha: k,
        draw: k,
      });
    }
  }

  // --- siding: the right wall's sheets, then the front's, gable and all.
  const right = wallOf("right");
  const front = wallOf("front");
  if (progress.siding > 0) {
    const sheets: { wall: Wall; u0: number; u1: number }[] = [];
    for (let u = 0; u < d; u += 4) {
      sheets.push({ wall: right, u0: u, u1: Math.min(d, u + 4) });
    }
    for (let u = 0; u < w; u += 4) {
      sheets.push({ wall: front, u0: u, u1: Math.min(w, u + 4) });
    }
    sheets.forEach((sheet, i) => {
      const k = appear(progress.siding, i, sheets.length, STAGING.siding.overlap);
      if (k <= 0) {
        return;
      }
      const slap = 1 - ease.back(k);
      drawSidingSheet(ink, proj, shape, sheet.wall, sheet.u0, sheet.u1, k, slap, fine);
    });
    // The openings, cut through the sheets: dark inside, glass in the windows.
    const doorway = front.openings[0];
    if (doorway !== undefined && span(progress.siding, 0.5, 0.9) > 0) {
      ink.shape(rect(front, 1, doorway), { fill: p.ink, fillAlpha: 0.78, width: fine ? 1.2 : 2.2, offset: 0 });
    }
    if (span(progress.siding, 0.1, 0.4) > 0) {
      for (const opening of right.openings) {
        drawWindow(ink, rect(right, 1, opening), fine, layers.glow ?? 0);
      }
    }
  }

  // --- trim: the corner boards, in white. Before the roof, because they are on the walls
  // and the roof overhangs them: drawn after it, their tops ran up over the eave and the
  // rake board.
  if (progress.trim > 0) {
    const k = progress.trim;
    const trim: Paint = { fill: p.trim, width: fine ? 1.2 : 2, overshoot: false, offset: 0.6, draw: k };
    const corner = 0.32;
    const top = floor + shape.wall;
    ink.shape([at(0, floor, 0), at(corner, floor, 0), at(corner, roofHeight(shape, corner), 0), at(0, roofHeight(shape, 0), 0)], trim);
    ink.shape(
      [at(w - corner, floor, 0), at(w, floor, 0), at(w, roofHeight(shape, w), 0), at(w - corner, roofHeight(shape, w - corner), 0)],
      trim,
    );
    ink.shape([at(w, floor, d - corner), at(w, floor, d), at(w, Math.max(top, roofHeight(shape, w)), d), at(w, Math.max(top, roofHeight(shape, w)), d - corner)], trim);
  }

  // --- the roof: the deck, then shingles course by course from the eaves up.
  if (progress.roofDeck > 0) {
    drawRoof(ink, proj, shape, progress, ridgeX, ridgeY, eaveLeft, eaveRight, fine);
  }

  // --- the doors, hung last, and swinging out on their hinges.
  const doorway = front.openings[0];
  if (progress.doors > 0 && doorway !== undefined) {
    drawDoors(ink, proj, shape, doorway, progress.doors, progress.swing, fine);
  }

  return { frontTop: P(wallPoint(front, raised("front"), w * 0.42, shape.wall)) };
}

/**
 * A slab: four form boards set around the footprint, the concrete poured in from the back
 * and struck off level, the forms pulled, and the treated sills laid on it and bolted down.
 */
function drawSlab(ink: Ink, proj: Projection, shape: Shape, progress: Progress, fine: boolean): void {
  const p = ink.palette;
  const at = (x: number, y: number, z: number): Pt => project(proj, x, y, z);
  const { width: w, depth: d } = shape;
  const forms = span(progress.slab, 0, FORMS_SET);
  const pour = ease.inOut(span(progress.slab, FORMS_SET, SLAB_WORK.poured));
  const strike = span(progress.slab, SLAB_WORK.poured, 1);
  const pulled = span(progress.sills, 0, 0.3);
  const top = SLAB + FORM;
  const boards: [number, number, number, number][] = [
    [-FORM, w + FORM, d, d + FORM],
    [-FORM, 0, -FORM, d + FORM],
    [w, w + FORM, -FORM, d + FORM],
    [-FORM, w + FORM, -FORM, 0],
  ];
  const form = (i: number): void => {
    const k = appear(forms, i, boards.length, STAGING.runners.overlap);
    if (k <= 0 || pulled >= 1) {
      return;
    }
    const [x0, x1, z0, z1] = boards[i]!;
    const lift = (1 - ease.bounce(k)) * 3 + pulled * 2;
    box(ink, proj, x0, x1, lift, top + lift, z0, z1, p.wood, p.woodDark, fine);
  };
  form(0);
  form(1);

  // The concrete, poured from the back toward the front: wet and dark, lighter once struck.
  if (pour > 0) {
    const front = d * (1 - pour);
    const tone = { fill: p.roof, width: fine ? 1.1 : 1.8, overshoot: false, offset: 0.4 };
    ink.shape([at(w, 0, front), at(w, 0, d), at(w, SLAB, d), at(w, SLAB, front)], { ...tone, fillAlpha: 0.55 });
    ink.shape([at(0, SLAB, front), at(w, SLAB, front), at(w, SLAB, d), at(0, SLAB, d)], {
      ...tone,
      fillAlpha: mix(0.5, 0.28, strike),
    });
    ink.shape([at(0, 0, front), at(w, 0, front), at(w, SLAB, front), at(0, SLAB, front)], { ...tone, fillAlpha: 0.6 });
    // The screed board's pass: a bright line drawn back across the wet top.
    if (strike > 0 && strike < 1) {
      const z = d * strike;
      const a = at(-0.2, SLAB + 0.05, z);
      const b = at(w + 0.2, SLAB + 0.05, z);
      ink.line(a.x, a.y, b.x, b.y, { width: fine ? 1.4 : 3, color: p.trim, overshoot: false });
    }
  }
  form(2);
  form(3);

  // The sills: treated lumber, so tinted, laid around the edge and bolted down.
  if (progress.sills > 0) {
    const pieces = sillPieces(shape);
    pieces.forEach(([x0, x1, z0, z1], i) => {
      const k = appear(progress.sills, i, pieces.length, STAGING.joists.overlap);
      if (k <= 0) {
        return;
      }
      const lift = (1 - ease.bounce(k)) * 0.6;
      box(ink, proj, x0, x1, SLAB + lift, SLAB + SILL + lift, z0, z1, p.grass, p.siding, fine);
      if (k >= 1 && !fine) {
        const long = x1 - x0 > z1 - z0;
        const length = long ? x1 - x0 : z1 - z0;
        for (let along = 1; along < length - 0.4; along += 4) {
          const bolt = long ? at(x0 + along, SLAB + SILL, (z0 + z1) / 2) : at((x0 + x1) / 2, SLAB + SILL, z0 + along);
          ink.dot(bolt.x, bolt.y, 2.4, p.roof);
        }
      }
    });
  }
}

/** The cap plates go on in a flash of the accent, then settle back to wood. */
function mixColor(p: Ink["palette"], caps: number): string {
  return caps > 0.05 && caps < 0.95 ? p.accent : p.wood;
}

function box(
  ink: Ink,
  proj: Projection,
  x0: number,
  x1: number,
  y0: number,
  y1: number,
  z0: number,
  z1: number,
  fill: string,
  side: string,
  fine: boolean,
): void {
  const at = (x: number, y: number, z: number): Pt => project(proj, x, y, z);
  const paint: Paint = { width: fine ? 1.1 : 1.8, overshoot: false, offset: 0.6 };
  ink.shape([at(x1, y0, z0), at(x1, y0, z1), at(x1, y1, z1), at(x1, y1, z0)], { ...paint, fill: side });
  ink.shape([at(x0, y1, z0), at(x1, y1, z0), at(x1, y1, z1), at(x0, y1, z1)], { ...paint, fill });
  ink.shape([at(x0, y0, z0), at(x1, y0, z0), at(x1, y1, z0), at(x0, y1, z0)], { ...paint, fill });
}

function drawSidingSheet(
  ink: Ink,
  proj: Projection,
  shape: Shape,
  wallDef: Wall,
  u0: number,
  u1: number,
  k: number,
  slap: number,
  fine: boolean,
): void {
  const p = ink.palette;
  const at = (u: number, y: number): Pt => {
    const v = add(wallDef.origin, wallDef.along, u);
    // A sheet arriving stands a little proud of the wall and settles onto it.
    const out = wallDef.name === "front" ? { x: 0, y: 0, z: -slap * 0.8 } : { x: slap * 0.8, y: 0, z: 0 };
    return project(proj, v.x + out.x, y, v.z + out.z);
  };
  const xAt = (u: number): number => add(wallDef.origin, wallDef.along, u).x;
  const topAt = (u: number): number =>
    wallDef.name === "front" ? roofHeight(shape, xAt(u)) - 0.05 : Math.max(floorOf(shape) + shape.wall, roofHeight(shape, shape.width) - 0.05);
  const bottom = sidingBottom(shape);
  const outline: Pt[] = [at(u0, bottom), at(u1, bottom), at(u1, topAt(u1))];
  const ridgeU = shape.width * shape.ridge;
  if (wallDef.name === "front" && ridgeU > u0 && ridgeU < u1) {
    outline.push(at(ridgeU, topAt(ridgeU)));
  }
  outline.push(at(u0, topAt(u0)));
  ink.shape(outline, { fill: p.siding, fillAlpha: clamp(k * 1.5), width: fine ? 1.2 : 2.2, overshoot: !fine, offset: 1.4 });
  // Grooves every eight inches, as T1-11 is milled.
  if (!fine && k > 0.6) {
    for (let u = u0 + 2 / 3; u < u1 - 0.1; u += 2 / 3) {
      const a = at(u, bottom + 0.15);
      const b = at(u, topAt(u) - 0.15);
      ink.line(a.x, a.y, b.x, b.y, { width: 1.1, alpha: 0.28, overshoot: false, wobble: 0.5 });
    }
  }
}

function drawWindow(ink: Ink, corners: Pt[], fine: boolean, glow: number): void {
  const p = ink.palette;
  ink.shape(corners, { fill: p.glass, width: fine ? 1.2 : 2.4, offset: 0 });
  if (glow > 0) {
    ink.wash(corners, p.accent, 0.75 * glow, 0);
  }
  const [a, b, c, dd] = corners as [Pt, Pt, Pt, Pt];
  const mid = (m: Pt, n: Pt): Pt => ({ x: (m.x + n.x) / 2, y: (m.y + n.y) / 2 });
  const muntin = { width: fine ? 1 : 2.4, color: p.trim, overshoot: false };
  const top = mid(dd, c);
  const bottom = mid(a, b);
  const left = mid(a, dd);
  const right = mid(b, c);
  ink.line(top.x, top.y, bottom.x, bottom.y, muntin);
  ink.line(left.x, left.y, right.x, right.y, muntin);
  if (!fine) {
    // The glint.
    const g0 = { x: a.x + (c.x - a.x) * 0.15, y: a.y + (c.y - a.y) * 0.25 };
    const g1 = { x: a.x + (c.x - a.x) * 0.32, y: a.y + (c.y - a.y) * 0.08 };
    ink.line(g0.x, g0.y - 6, g1.x, g1.y - 2, { width: 2, color: p.trim, alpha: 0.8, overshoot: false });
  }
  ink.poly(corners, true, { width: fine ? 1.4 : 4, color: p.trim, overshoot: false });
  ink.poly(corners, true, { width: fine ? 0.8 : 1.4, overshoot: !fine });
}

function drawRoof(
  ink: Ink,
  proj: Projection,
  shape: Shape,
  progress: Progress,
  ridgeX: number,
  ridgeY: number,
  eaveLeft: { x: number; y: number },
  eaveRight: { x: number; y: number },
  fine: boolean,
): void {
  const p = ink.palette;
  const at = (x: number, y: number, z: number): Pt => project(proj, x, y, z);
  const z0 = -OVERHANG * 0.8;
  const z1 = shape.depth + OVERHANG * 0.8;
  const planes = [
    { from: eaveLeft, to: { x: ridgeX, y: ridgeY } },
    { from: eaveRight, to: { x: ridgeX, y: ridgeY } },
  ].filter((plane) => Math.abs(plane.from.x - plane.to.x) > 0.05);
  const deckK = progress.roofDeck;
  for (const [i, plane] of planes.entries()) {
    const k = span(deckK, i * 0.35, i * 0.35 + 0.65);
    if (k <= 0) {
      continue;
    }
    const lift = (1 - ease.back(k)) * 1.5;
    const corners = [
      at(plane.from.x, plane.from.y + lift, z0),
      at(plane.to.x, plane.to.y + lift, z0),
      at(plane.to.x, plane.to.y + lift, z1),
      at(plane.from.x, plane.from.y + lift, z1),
    ];
    ink.shape(corners, { fill: p.wood, fillAlpha: k, width: fine ? 1.2 : 2.2, overshoot: !fine });
    // Shingles: courses from the eave up, each a row of tabs.
    const courses = fine ? 5 : 9;
    const done = progress.shingles * courses;
    for (let c = 0; c < courses; c += 1) {
      const kc = clamp(done - c);
      if (kc <= 0) {
        break;
      }
      const a = c / courses;
      const b = (c + 1) / courses;
      const lerp = (m: number): { x: number; y: number } => ({
        x: plane.from.x + (plane.to.x - plane.from.x) * m,
        y: plane.from.y + (plane.to.y - plane.from.y) * m,
      });
      const lo = lerp(a);
      const hi = lerp(Math.min(1, b + 0.04));
      const reach = z0 + (z1 - z0) * kc;
      ink.shape([at(lo.x, lo.y, z0), at(hi.x, hi.y, z0), at(hi.x, hi.y, reach), at(lo.x, lo.y, reach)], {
        fill: p.roof,
        width: fine ? 0.8 : 1.6,
        overshoot: false,
        offset: 0.5,
      });
      if (!fine) {
        for (let z = z0 + (c % 2 === 0 ? 0.6 : 1.2); z < reach - 0.3; z += 1.2) {
          const m = at(lo.x, lo.y, z);
          const n = at(lo.x + (hi.x - lo.x) * 0.7, lo.y + (hi.y - lo.y) * 0.7, z);
          ink.line(m.x, m.y, n.x, n.y, { width: 1.1, alpha: 0.5, overshoot: false, wobble: 0.4 });
        }
      }
    }
  }
  // The rake board across the front and the cap along the ridge.
  if (deckK > 0.6 && !fine) {
    const rake = { width: 5, color: p.trim, overshoot: false };
    for (const plane of planes) {
      const m = at(plane.from.x, plane.from.y, z0);
      const n = at(plane.to.x, plane.to.y, z0);
      ink.line(m.x, m.y + 3, n.x, n.y + 3, rake);
      ink.line(m.x, m.y + 6, n.x, n.y + 6, { width: 1.4, overshoot: false });
    }
  }
  if (progress.shingles > 0.95) {
    const m = at(ridgeX, ridgeY, z0);
    const n = at(ridgeX, ridgeY, z1);
    ink.line(m.x, m.y, n.x, n.y, { width: fine ? 2 : 4.5 });
  }
}

function drawDoors(
  ink: Ink,
  proj: Projection,
  shape: Shape,
  opening: Rect,
  hung: number,
  swing: number,
  fine: boolean,
): void {
  const p = ink.palette;
  const leaves = shape.doubleDoor ? 2 : 1;
  const leafWidth = (opening.u1 - opening.u0) / leaves;
  const top = floorOf(shape) + opening.v1 - 0.05;
  const bottom = floorOf(shape) + 0.05;
  const open = (Math.PI / 2) * 0.82 * ease.inOut(swing);
  for (let i = 0; i < leaves; i += 1) {
    const k = span(hung, i * 0.3, i * 0.3 + 0.7);
    if (k <= 0) {
      continue;
    }
    const hingeLeft = i === 0;
    const hinge = hingeLeft ? opening.u0 : opening.u1;
    const dirX = hingeLeft ? 1 : -1;
    const reachX = hinge + dirX * leafWidth * Math.cos(open);
    const reachZ = -leafWidth * Math.sin(open);
    const at = (x: number, y: number, z: number): Pt => project(proj, x, y, z);
    const corners = [at(hinge, bottom, 0), at(reachX, bottom, reachZ), at(reachX, top, reachZ), at(hinge, top, 0)];
    const drop = (1 - ease.back(k)) * 0.6;
    const shifted = corners.map((c) => ({ x: c.x, y: c.y - drop * proj.scale }));
    ink.shape(shifted, { fill: p.accent, width: fine ? 1.2 : 2.4, offset: 1 });
    if (!fine) {
      // The Z: two rails and a brace, in white.
      const lerp = (a: Pt, b: Pt, m: number): Pt => ({ x: a.x + (b.x - a.x) * m, y: a.y + (b.y - a.y) * m });
      const [a, b, c, d] = shifted as [Pt, Pt, Pt, Pt];
      const lowL = lerp(a, d, 0.14);
      const lowR = lerp(b, c, 0.14);
      const highL = lerp(a, d, 0.86);
      const highR = lerp(b, c, 0.86);
      const rail = { width: 4.5, color: p.trim, overshoot: false };
      ink.line(lowL.x, lowL.y, lowR.x, lowR.y, rail);
      ink.line(highL.x, highL.y, highR.x, highR.y, rail);
      ink.line(lowL.x, lowL.y, highR.x, highR.y, rail);
      const handle = lerp(lerp(a, b, 0.86), lerp(d, c, 0.86), 0.5);
      ink.dot(handle.x, handle.y, 3);
    }
  }
}

/** The middle of the front wall's top plate, raised by `raise`: where hands lift it from. */
export function frontTop(proj: Projection, shape: Shape, raise: number, along = 0.42): Pt {
  const front = walls(shape).find((wall) => wall.name === "front")!;
  const v = wallPoint(front, ease.out(raise), shape.width * along, shape.wall);
  return project(proj, v.x, v.y, v.z);
}

/** A whole build squeezed into one number: every stage in order, from nothing to done. */
export function quickBuild(q: number): Progress {
  const stage = (i: number, n = 9): number => clamp(q * n - i);
  const wall = (i: number): WallProgress => ({ frame: stage(i), raise: stage(i + 0.5) });
  return {
    runners: stage(0),
    joists: stage(0.6),
    deck: stage(1.3),
    slab: stage(0, 4.5),
    sills: stage(1.3),
    left: wall(2),
    back: wall(2.3),
    right: wall(2.6),
    front: wall(2.9),
    caps: 0,
    rafters: stage(4),
    siding: stage(5),
    roofDeck: stage(6),
    shingles: stage(6.5),
    trim: stage(7.5),
    doors: stage(8),
    swing: 0,
  };
}
